import { observer } from 'mobx-react-lite';
import * as jwt from 'jsonwebtoken';
import { PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserStore } from 'src/context';
import { toast } from 'react-toastify';
import { Loader } from '@talon/web/src/components/ui';

export type RequireAuthProps = PropsWithChildren & {
  adminOnly?: boolean;
  supportAgentOnly?: boolean;
};

export const RequireAuth = observer(({ children, adminOnly, supportAgentOnly }: RequireAuthProps) => {
  /** Context **/
  const { token, user, logout, isBootstrapping } = useUserStore();
  const location = useLocation();

  /** State **/
  const [destination, setDestination] = useState<string | undefined>(undefined);

  /** Effects **/
  useEffect(() => {
    // The initial token/user restore from storage is still in flight — wait for it instead of
    // guessing from `token`/`user`'s transient placeholder values (see specs/unified-chat.md
    // BUG-4: `_user` starts as `{}` and `_jwt` starts as `''` regardless of whether a real
    // session exists, so those alone can't distinguish "still loading" from "logged out").
    if (isBootstrapping) return;

    const exp = jwt.decode(token || '', { json: true })?.exp;
    const valid = exp ? exp * 1000 > Date.now() : false;
    if (!token || !valid) {
      setDestination('/login');
      logout();
      // Only claim a session "expired" if one actually existed — a first-time/logged-out
      // visitor with no token was never in a session to begin with.
      if (token) {
        toast.error('Your session has expired. Please login again.', { toastId: 'session-expired' });
      }
    } else if (adminOnly && !user.is_admin) {
      setDestination('/');
    } else if (supportAgentOnly && !user.is_admin && !user.is_support_agent) {
      setDestination('/');
    } else {
      setDestination(undefined);
    }
  }, [token, user, adminOnly, supportAgentOnly, logout, isBootstrapping]);

  /** Render **/
  if (isBootstrapping) return <Loader />;
  return <>{destination ? <Navigate to={destination} state={{ from: location }} replace /> : children}</>;
});
